import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import Box from "@material-ui/core/Box";
import Avatar from "@material-ui/core/Avatar";
import InputBase from "@material-ui/core/InputBase";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import SearchIcon from "@material-ui/icons/Search";
import { parsePhoneNumber } from "libphonenumber-js";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError"; 
import ChatiaDialog from "../ChatiaDialog";
import CustomToggleSwitch from "../CustomToggleSwitch";
import PhoneNumberDisplay from "../PhoneNumberDisplay";
import { filterValidContacts } from "../../utils/contactValidation";

const useStyles = makeStyles((theme) => ({
	content: {
		display: "flex",
		flexDirection: "column",
		gap: 12,
		width: "100%",
		boxSizing: "border-box",
		height: "min(640px, calc(100vh - 220px))",
		minHeight: 360,
		[theme.breakpoints.down("xs")]: {
			height: "calc(100vh - 190px)",
		},
	},
	searchWrap: {
		margin: "0 0 6px",
		height: 44,
		width: "100%",
		boxSizing: "border-box",
		borderRadius: 999,
		border: theme.mode === "light" ? "1px solid #d1d7db" : "1px solid #3b4a54",
		backgroundColor: theme.mode === "light" ? "#ffffff" : "rgba(255,255,255,0.03)",
		display: "flex",
		alignItems: "center",
		padding: "0 16px",
		gap: 10,
		flexShrink: 0,
		color: theme.mode === "light" ? "#54656f" : "#aebac1",
		"&:focus-within": {
			borderColor: theme.mode === "light" ? "#aebac1" : "#667781",
		},
	},
	searchInput: {
		flex: 1,
		fontSize: 14,
		color: theme.mode === "light" ? "#111b21" : "#e9edef",
		"& input::placeholder": {
			color: theme.mode === "light" ? "#667781" : "#8696a0",
			opacity: 1,
		},
	},
	list: {
		flex: 1,
		minHeight: 0,
		overflowY: "auto",
		overflowX: "hidden",
		display: "flex",
		flexDirection: "column",
		gap: 8,
		marginRight: -24,
		paddingRight: 24,
		...theme.scrollbarStyles,
	},
	checkboxSlot: {
		width: 56,
		display: "flex",
		justifyContent: "center",
		flexShrink: 0,
	},
	checkboxLabel: {
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		cursor: "pointer",
		userSelect: "none",
	},
	cyberpunkCheckbox: {
		appearance: "none",
		WebkitAppearance: "none",
		MozAppearance: "none",
		width: 16,
		height: 16,
		border: `2px solid ${theme.palette.primary.main}`,
		borderRadius: 5,
		backgroundColor: "transparent",
		display: "inline-block",
		position: "relative",
		margin: 0,
		padding: 0,
		cursor: "pointer",
		flexShrink: 0,
		verticalAlign: "middle",
		"&::before": {
			content: '""',
			backgroundColor: theme.palette.primary.main,
			display: "block",
			position: "absolute",
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%) scale(0)",
			width: 8,
			height: 8,
			borderRadius: 3,
			transition: "all 0.25s ease-in-out",
		},
		"&:checked::before": {
			transform: "translate(-50%, -50%) scale(1)",
		},
	},
	contactRow: {
		display: "flex",
		alignItems: "center",
		minHeight: 72,
		padding: "5px 0",
		borderRadius: 8,
		width: "100%",
		boxSizing: "border-box",
		flexShrink: 0,
		cursor: "pointer",
		transition: "background-color 0.15s ease",
		"&:hover": {
			backgroundColor: theme.mode === "light" ? "#f5f6f6" : "rgba(255,255,255,0.05)",
		},
	},
	selectedRow: {
		backgroundColor: theme.mode === "light" ? "#f0f2f5" : "rgba(255,255,255,0.08)",
	},
	contactAvatar: {
		width: 48,
		height: 48,
		flexShrink: 0,
		fontSize: 17,
		fontWeight: 600,
		backgroundColor: "#d9fdd3",
		color: "#008069",
		marginRight: 14,
	},
	contactInfo: {
		minWidth: 0,
		flex: 1,
		paddingRight: 8,
	},
	contactName: {
		fontSize: 16,
		fontWeight: 400,
		color: theme.mode === "light" ? "#111b21" : "#e9edef",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		lineHeight: "22px",
	},
	contactNumber: {
		fontSize: 14,
		color: theme.mode === "light" ? "#667781" : "#8696a0",
		marginTop: 2,
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	sectionLabel: {
		fontSize: 14,
		color: theme.mode === "light" ? "#667781" : "#8696a0",
		margin: "0 0 8px 14px",
	},
	addContactRow: {
		display: "flex",
		alignItems: "center",
		minHeight: 64,
		borderRadius: 8,
		cursor: "pointer",
		paddingLeft: 56,
		color: "#008069",
		fontSize: 15,
		fontWeight: 500,
		"&:hover": {
			backgroundColor: theme.mode === "light" ? "#f5f6f6" : "rgba(255,255,255,0.05)",
		},
	},
	addIcon: {
		width: 40,
		height: 40,
		borderRadius: "50%",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#00a884",
		color: "#ffffff",
		marginRight: 14,
		flexShrink: 0,
	},
	emptyState: {
		minHeight: 100,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: theme.mode === "light" ? "#667781" : "#8696a0",
		fontSize: 14,
	},
	loadingRow: {
		minHeight: 100,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#00a884",
	},
	selectedCount: {
		flex: 1,
		fontSize: 13,
		color: theme.mode === "light" ? "#54656f" : "#aebac1",
	},
	signControl: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		color: theme.mode === "light" ? "#667781" : "#aebac1",
		fontSize: 12,
		fontWeight: 500,
		userSelect: "none",
	},
	signToggle: {
		"--switch-width": "42px",
		"--switch-height": "22px",
	},
	footerMeta: {
		marginRight: "auto",
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: 12,
		minWidth: 0,
	},
	sendingText: {
		fontSize: 12,
		color: theme.mode === "light" ? "#667781" : "#aebac1",
		marginLeft: 4,
	},
}));

const ForwardMessageModal = ({ messages, onClose, modalOpen }) => {
	const classes = useStyles();
    const [optionsContacts, setOptionsContacts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContacts, setSelectedContacts] = useState([]);
	const [newContact, setNewContact] = useState({});
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [sending, setSending] = useState(false);
	const [messageSending, setMessageSending] = useState('');
	const [signMessage, setSignMessage] = useState(true);

	const translate = (key, fallback, options) => {
		const value = i18n.t(key, options);
		return value === key ? fallback : value;
	};

	const isForwardableContact = contact => {
		if (!contact?.number) return false;
		try {
			let phone = contact.number.toString().trim();
			if (!phone.startsWith("+")) phone = `+${phone}`;
			if (/^\+521\d{10}$/.test(phone)) {
				phone = phone.replace(/^\+521/, "+52");
			}
			const parsedNumber = parsePhoneNumber(phone);
			return !!parsedNumber && parsedNumber.isValid();
		} catch (error) {
			return false;
		}
	};

    useEffect(() => {
		if (!modalOpen) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { searchParam, isGroup: "false" },
					});
					setOptionsContacts(filterValidContacts(data.contacts || []).filter(isForwardableContact));
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	const history = useHistory();

	const sleep = (ms) => {
		return new Promise(resolve => setTimeout(resolve, ms));
	};

    const handleForwardMessage = async(contactL) => {
		setSending(true);
		for (const contact of selectedContacts) {
			for (const message of messages) {
				try {
					setMessageSending(`${message.id} -> ${contact.name}`);
					await api.post('/message/forward', {messageId: message.id, contactId: contact.id, signMessage: signMessage});
					await sleep(900);
				} catch (error) {
					toastError(error);
				}
			}
		}
		setSending(false);

		history.push('/tickets');
    }

	const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedContacts([]);
		setOptionsContacts([]);
		setSending(false);
	};

    const handleCloseContactModal = () => {
		setContactModalOpen(false);
	};

	const handleAddContact = () => {
		if (!searchParam.trim()) return;
		setNewContact({ name: searchParam.trim() });
		setContactModalOpen(true);
	};

	const toggleContact = contact => {
		setSelectedContacts(prev => {
			const exists = prev.some(item => item.id === contact.id);
			if (exists) {
				return prev.filter(item => item.id !== contact.id);
			}
			return [...prev, contact];
		});
	};

	const renderContact = contact => {
		const isSelected = selectedContacts.some(item => item.id === contact.id);
		const avatarUrl = contact.urlPicture || contact.profilePicUrl || contact.pictureUrl;
		const initial = (contact.name || contact.number || "?").trim().charAt(0).toUpperCase();

		return (
			<Box
				key={contact.id}
				className={`${classes.contactRow} ${isSelected ? classes.selectedRow : ""}`}
				onClick={() => toggleContact(contact)}
			>
				<Box className={classes.checkboxSlot}>
					<label className={classes.checkboxLabel}>
						<input
							type="checkbox"
							className={classes.cyberpunkCheckbox}
							checked={isSelected}
							onChange={() => toggleContact(contact)}
							onClick={(e) => e.stopPropagation()}
						/>
					</label>
				</Box>
				<Avatar src={avatarUrl} className={classes.contactAvatar}>
					{initial}
				</Avatar>
				<Box className={classes.contactInfo}>
					<Typography className={classes.contactName}>{contact.name}</Typography>
					<Typography className={classes.contactNumber} component="div">
						<PhoneNumberDisplay phoneNumber={contact.number} />
					</Typography>
				</Box>
			</Box>
		);
	};

    return (
        <>
			<ContactModal
				open={contactModalOpen}
				initialValues={newContact}
				onClose={handleCloseContactModal}
			></ContactModal>
			<ChatiaDialog
				open={modalOpen}
				onClose={handleClose}
				title={translate("forwardMessageModal.title", "Forward messages to")}
				maxWidth="xs"
				contentDividers
				onSave={handleForwardMessage}
				saveText={translate("forwardMessageModal.buttons.ok", "Forward")}
				saveDisabled={!selectedContacts.length || sending}
				saveLoading={sending}
				footerChildren={
					<Box className={classes.footerMeta}>
						<Typography className={classes.selectedCount}>
							{selectedContacts.length
								? translate(
									selectedContacts.length === 1
										? "forwardMessageModal.selectedRecipient"
										: "forwardMessageModal.selectedRecipients",
									`${selectedContacts.length} selected ${selectedContacts.length === 1 ? "recipient" : "recipients"}`,
									{ count: selectedContacts.length }
								)
								: translate(
									messages.length === 1
										? "forwardMessageModal.selectedMessage"
										: "forwardMessageModal.selectedMessages",
									`${messages.length} selected ${messages.length === 1 ? "message" : "messages"}`,
									{ count: messages.length }
								)}
							{sending && (
								<span className={classes.sendingText}>
									{translate("forwardMessageModal.sending", "Sending {{target}}...", { target: messageSending })}
								</span>
							)}
						</Typography>
						<label className={classes.signControl}>
							<span>{i18n.t("messagesInput.signMessage")}</span>
							<CustomToggleSwitch
								className={classes.signToggle}
								checked={signMessage}
								onChange={(e) => {
									setSignMessage(e.target.checked);
								}}
							/>
						</label>
					</Box>
				}
			>
				<Box className={classes.content}>
					<Box className={classes.searchWrap}>
						<SearchIcon fontSize="small" />
						<InputBase
							className={classes.searchInput}
							value={searchParam}
							autoFocus
							placeholder={translate("forwardMessageModal.searchPlaceholder", "Search name or number")}
							onChange={e => setSearchParam(e.target.value)}
						/>
					</Box>

					<Typography className={classes.sectionLabel}>
						{translate("forwardMessageModal.recentConversations", "Recent conversations")}
					</Typography>

					<Box className={classes.list}>
						{loading ? (
							<Box className={classes.loadingRow}>
								<CircularProgress color="inherit" size={24} />
							</Box>
						) : (
							<>
								{optionsContacts.map(renderContact)}

								{!optionsContacts.length && searchParam.trim().length >= 3 && (
									<Box className={classes.addContactRow} onClick={handleAddContact}>
										<span className={classes.addIcon}>
											<PersonAddIcon fontSize="small" />
										</span>
										{translate("forwardMessageModal.createContact", "Create contact \"{{name}}\"", { name: searchParam.trim() })}
									</Box>
								)}

								{!optionsContacts.length && searchParam.trim().length < 3 && (
									<Box className={classes.emptyState}>
										{translate("forwardMessageModal.typeToSearch", "Type to search contacts.")}
									</Box>
								)}
							</>
						)}
					</Box>
				</Box>
			</ChatiaDialog>
		</>
    );
};

export default ForwardMessageModal;
